import { FunctionComponent, PropsWithChildren } from 'react';
import { EnvService } from '@hub/services/core/env-service';
import { Environments } from '@monorepo/client-types';
import {
  FeatureFlagClient,
  FeatureFlagInitOptions,
  FeatureFlagProvider as FFProvider,
  initFeatureFlagCache,
} from '@monorepo/feature-flag/client';
import {
  FeatureFlagAccessTokenKey,
  FeatureFlagAccessTokenParamKey,
} from '@monorepo/types';

const {
  isProduction,
  featureFlagApiUrl,
  featureFlagClientKey,
  appEnv,
  ffCacheInterval,
} = EnvService.get();

initFeatureFlagCache({
  staleTTL: ffCacheInterval,
  maxAge: ffCacheInterval,
  disableIdleStreams: false,
});

const getGrowthBookEnvironment = (): string => {
  if (appEnv === Environments.Production) {
    return 'production';
  }

  // Map 'earth' to 'earth' for GrowthBook targeting
  if (appEnv === Environments.Earth) {
    return 'earth';
  }

  // Map 'staging' to 'staging' for GrowthBook targeting
  if (appEnv === Environments.Staging) {
    return 'staging';
  }

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }

  if (hostname.includes('dev')) {
    return 'dev';
  }

  return 'local';
};

const growthBookEnvironment = getGrowthBookEnvironment();

// Extract access token synchronously BEFORE GrowthBook initializes
// Same pattern as admin_token extraction in use-app-initialization.ts
const getAccessToken = (): string | undefined => {
  const queryAccessToken = new URL(window.location.href).searchParams.get(
    FeatureFlagAccessTokenParamKey,
  );
  const accessToken =
    queryAccessToken ||
    localStorage.getItem(FeatureFlagAccessTokenKey) ||
    undefined;

  if (queryAccessToken) {
    localStorage.setItem(FeatureFlagAccessTokenKey, queryAccessToken);
  }

  return accessToken;
};

const initialAccessToken =
  typeof window !== 'undefined' ? getAccessToken() : undefined;

const featureFlag = new FeatureFlagClient({
  config: {
    apiHost: featureFlagApiUrl,
    clientKey: featureFlagClientKey,
    disableVisualExperiments: true,
    disableJsInjection: true,
    disableUrlRedirectExperiments: true,
    disableCache: !isProduction,
    attributes: {
      environment: growthBookEnvironment,
      ...(initialAccessToken && { accessToken: initialAccessToken }),
    },
  },
  devMode: !isProduction,
});

const initOptions: FeatureFlagInitOptions = {
  streaming: false,
  timeout: 5000,
};

export const FeatureFlagProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  return (
    <FFProvider
      settings={{
        featureFlag,
        initOptions,
      }}
    >
      {children}
    </FFProvider>
  );
};
